import { formatNumber, formatPercent } from "@utils/formatUtils";
import {
  HEDGE_MARGIN_STATUS,
  MARGIN_SAFE_THRESHOLD,
  MARGIN_CAUTION_THRESHOLD,
  RATIO_MARKER_MIN,
  RATIO_MARKER_MAX,
  MAP_TICKER_TO_NAME,
  TIER_STYLE_MAP,
  TIER_RANK,
  TIER_NAV_RANGE,
  FEATURE_MIN_TIER,
  LOCKED_ASSET_META,
  CAPITAL_AMOUNT_MIN,
  CAPITAL_AMOUNT_MAX,
  HEDGING_MIN_CAPITAL,
  DEFAULT_PROFILE,
} from "./nebulaData";
import { parseDate, relativeDayLabel, formatMonthYear } from "./dateUtils";

export const formatPnlPercent = (value) => {
  const num = typeof value === "number" ? value : parseFloat(value);
  if (isNaN(num)) return "--";
  const sign = num > 0 ? "+" : "";
  return `${sign}${num.toFixed(1)}%`;
};

export const buildProfileValues = (profile) => {
  const values = { ...DEFAULT_PROFILE };
  for (const field in DEFAULT_PROFILE) {
    if (profile?.[field] !== undefined) values[field] = profile[field];
  }
  return values;
};

export const validateProfile = (values, tier) => {
  const amount = values.capitalAmount || 0;
  const { min, max } = TIER_NAV_RANGE[tier?.toUpperCase()] ?? {};
  const fmt = formatMoneyVND;

  const rules = [
    [amount < CAPITAL_AMOUNT_MIN, `Số vốn đầu tư tối thiểu là ${fmt(CAPITAL_AMOUNT_MIN)}.`],
    [amount > CAPITAL_AMOUNT_MAX, `Số vốn đầu tư tối đa là ${fmt(CAPITAL_AMOUNT_MAX)}.`],
    [min != null && amount < min, `Gói ${tier} yêu cầu số vốn từ ${fmt(min)}.`],
    [
      max != null && max < CAPITAL_AMOUNT_MAX && amount >= max,
      `Gói ${tier} chỉ hỗ trợ số vốn dưới ${fmt(max)}.`,
    ],
    [
      values.hedgingEnabled && amount < HEDGING_MIN_CAPITAL,
      `Để bật Hedging VN30, số vốn tối thiểu là ${fmt(HEDGING_MIN_CAPITAL)}.`,
    ],
  ];

  const capitalAmount = rules.find(([failed]) => failed)?.[1];
  return capitalAmount ? { capitalAmount } : {};
};

export const getChangedProfileFields = (values, original = {}) =>
  Object.fromEntries(
    Object.entries(values).filter(
      ([field, value]) => original[field] !== value,
    ),
  );

export const getTierStyle = (tier) =>
  TIER_STYLE_MAP[tier?.toUpperCase()] ?? TIER_STYLE_MAP.default;

export const getTierRank = (tier) => TIER_RANK[tier?.toUpperCase()] ?? 0;

export const isDowngrade = (fromTier, toTier) =>
  getTierRank(toTier) < getTierRank(fromTier);

export const isFeatureLocked = (tier, key) => {
  const minTier = FEATURE_MIN_TIER[key];
  if (!minTier) return false;
  return getTierRank(tier) < TIER_RANK[minTier];
};

export const getLockedAsset = (ticker, tier) => {
  const meta = LOCKED_ASSET_META[ticker];
  if (!meta || !isFeatureLocked(tier, meta.feature)) return null;
  return { message: meta.message, requiredTier: FEATURE_MIN_TIER[meta.feature] };
};

export const getTickerLabel = (ticker) => MAP_TICKER_TO_NAME[ticker] || ticker;

export const isFixedIncomeTab = (tab) => tab === "fixed_income";

export const widenTickerColumn = (columns, tab, width = "1.3fr") =>
  isFixedIncomeTab(tab)
    ? columns.map((col) => (col.key === "ticker" ? { ...col, width } : col))
    : columns;

export const fixedIncomeMinWidth = (tab, base = "800px", wide = "900px") =>
  isFixedIncomeTab(tab) ? wide : base;

export const formatVND = (value, showSign = false) => {
  if (value === null || value === undefined || isNaN(value)) return "0";
  const sign = showSign && value > 0 ? "+" : value < 0 ? "-" : "";
  return `${sign}${formatNumber(Math.abs(value))} đ`;
};

export const formatVNDShort = (value, millionLabel = "tr") => {
  if (value === null || value === undefined || isNaN(value)) return "0";
  const abs = Math.abs(value);
  if (abs >= 1_000_000_000) return `${(abs / 1_000_000_000).toFixed(1)}tỷ`;
  if (abs >= 1_000_000) return `${Math.round(abs / 1_000_000)}${millionLabel}`;
  return formatNumber(abs);
};

export const formatMoneyVND = (amount) => {
  if (!amount) return "—";
  if (amount >= 1_000_000_000) {
    const val = amount / 1_000_000_000;
    return `${Number.isInteger(val) ? val : val.toFixed(1)} tỷ`;
  }
  if (amount >= 1_000_000) {
    const val = amount / 1_000_000;
    return `${Number.isInteger(val) ? val : val.toFixed(0)} triệu`;
  }
  return `${amount.toLocaleString("vi-VN")} đồng`;
};

export const getHedgeStatus = (hedgeMetrics) => {
  if (hedgeMetrics?.callWarning === true)
    return HEDGE_MARGIN_STATUS.MARGIN_CALL;
  return String(hedgeMetrics?.status || "").toUpperCase();
};

export const getMarginStatus = (marginMetrics) => {
  if (marginMetrics?.callWarning === true)
    return HEDGE_MARGIN_STATUS.MARGIN_CALL;
  return String(
    marginMetrics?.status || HEDGE_MARGIN_STATUS.INACTIVE,
  ).toUpperCase();
};

export const getRatioState = (ratio) => {
  if (ratio == null || ratio === 0) return "safe";
  if (ratio > MARGIN_CAUTION_THRESHOLD) return "safe";
  if (ratio > MARGIN_SAFE_THRESHOLD) return "caution";
  return "critical";
};

export const getRatioMarkerPct = (ratio) => {
  if (ratio == null) return null;
  const pct =
    ((ratio - RATIO_MARKER_MIN) / (RATIO_MARKER_MAX - RATIO_MARKER_MIN)) * 100;
  return Math.max(0, Math.min(100, pct));
};

export const getMarginCardStyle = (status) => {
  if (status === HEDGE_MARGIN_STATUS.MARGIN_CALL) {
    return {
      surfaceClass: "bg-[#FFF7F7] border-[#FFE1E1]",
      isCritical: true,
      accentBarColor: "#ff6b6b",
    };
  }
  return {
    surfaceClass: "bg-white border-[#ECECED]",
    isCritical: false,
    accentBarColor: "#ECECED",
  };
};

export const getPnlColor = (value, defaultColor = "#111827") => {
  if (value > 0) return "#2aa97a";
  if (value < 0) return "#d95656";
  return defaultColor;
};

export const getPnlColorClass = (value) => `text-[${getPnlColor(value)}]`;

export const getHedgeStatusLabel = (status) => {
  if (status === HEDGE_MARGIN_STATUS.ACTIVE) return "Đang hoạt động";
  if (status === HEDGE_MARGIN_STATUS.MARGIN_CALL) return "Cảnh báo Margin call";
  if (status === HEDGE_MARGIN_STATUS.PENDING) return "Đang khởi động";
  return status;
};

export const getHedgeCardStyle = (status) => {
  if (status === HEDGE_MARGIN_STATUS.MARGIN_CALL) {
    return {
      surfaceClass: "bg-[#ff6b6b0d] border-[#ffe1e1]",
      badgeBg: "transparent",
      badgeColor: "#ff6b6b",
      accentBarColor: "#ff6b6b",
    };
  }
  if (status === HEDGE_MARGIN_STATUS.PENDING) {
    return {
      surfaceClass: "bg-transparent border-[#ECECED]",
      badgeBg: "#f5f5f5",
      badgeColor: "#636369",
      accentBarColor: "#ECECED",
    };
  }
  if (status === HEDGE_MARGIN_STATUS.INACTIVE) {
    return {
      surfaceClass: "bg-white border-[#ECECED]",
      badgeBg: "#34D3991A",
      badgeColor: "#34D399",
      accentBarColor: "#AEEDD6",
    };
  }
  return {
    surfaceClass: "bg-transparent border-[#ECECED]",
    badgeBg: "#34D3991A",
    badgeColor: "#34D399",
    accentBarColor: "#ECECED",
  };
};

export const getDecisionHeading = (blocks) => {
  const heading = (Array.isArray(blocks) ? blocks : []).find(
    (block) => block.type === "heading" && block.props?.level === 2,
  );
  return (heading?.content ?? [])
    .map((part) => part.text ?? "")
    .join("")
    .replace(/\s*[—–-]\s*\d{1,2}\/\d{1,2}\/\d{2,4}\s*$/, "")
    .trim();
};

export const groupByDate = (decisions) => {
  const groups = {};
  for (const item of decisions) {
    (groups[item.tradeDate] = groups[item.tradeDate] || []).push(item);
  }
  return Object.entries(groups).map(([date, entries]) => {
    const relative = relativeDayLabel(date);
    const isRelative = relative === "Hôm nay" || relative === "Hôm qua";
    return {
      date,
      label: (isRelative ? relative : date).toUpperCase(),
      entries,
    };
  });
};

export const methodSuffix = (method) => (method === "AVCO" ? "Avg" : "Fifo");

export const mapPositions = (data) => {
  if (!Array.isArray(data)) return [];
  return data.map((item) => ({
    ticker: item.ticker,
    buyDate: item.buyTime,
    sellDate: item.sellTime,
    quantity: item.quantity,
    buyPrice: item.buyPrice,
    currentPrice: item.priceClose || item.sellPrice,
    sellPrice: item.sellPrice,
    pnl: item.pnl,
    pnlPercent: item.pnlPercentage,
    holdingPeriod: item.holdingPeriod,
    assetClass: item.assetClass,
    status: item.status,
  }));
};

export const mapTransactions = (data) => {
  if (!Array.isArray(data)) return [];
  return data.map((item) => ({
    fillId: item.fillId,
    ticker: item.ticker,
    type: item.type,
    price: item?.priceClose ?? item?.price,
    quantity: item.quantity,
    timestamp: item.timestamp,
    assetClass: item.assetClass,
  }));
};

export const mapTickerHistory = (data) => {
  if (!Array.isArray(data)) return [];
  return data
    .map((item) => {
      const ts = parseDate(item.timestamp);
      return ts !== null ? [ts, item.priceClose ?? item.price] : null;
    })
    .filter(Boolean)
    .sort((a, b) => a[0] - b[0]);
};

const rateHigherBetter = (value, [good, average]) => {
  if (value >= good) return { label: "Rất tốt", type: "positive" };
  if (value >= average) return { label: "Tốt", type: "positive" };
  if (value >= average / 2) return { label: "Trung bình", type: "warning" };
  return { label: "Chưa đạt", type: "negative" };
};

export const rewardToRiskStatus = (value) => rateHigherBetter(value, [3, 2]);
export const profitFactorStatus = (value) => rateHigherBetter(value, [2, 1.5]);
export const sharpeStatus = (value) => rateHigherBetter(value, [2, 1]);

export const drawdownStatus = (value) => {
  const dd = Math.abs(value);
  if (dd <= 10) return { label: "Thấp", type: "positive" };
  if (dd <= 20) return { label: "Trung bình", type: "warning" };
  if (dd <= 35) return { label: "Chú ý", type: "warning" };
  return { label: "Cần thận trọng", type: "negative" };
};

export const buildBacktestMetrics = (metrics) => {
  if (!metrics) return null;
  const { finalReturn, annualizedReturn, sharpeRatio } = metrics;
  const fmtPercent = (v) => (v == null ? "—" : formatPercent(v, true));
  const fmtNumber = (v) => (v == null ? "—" : formatNumber(v, 1));
  return [
    {
      label: "Tổng lợi nhuận",
      value: fmtPercent(finalReturn),
      color: getPnlColor(finalReturn || 0),
      subLabel: metrics.firstTradingDate
        ? `Từ ${formatMonthYear(metrics.firstTradingDate)}`
        : "",
    },
    {
      label: "Lãi kép",
      value: fmtPercent(annualizedReturn),
      color: getPnlColor(annualizedReturn || 0),
      subLabel: "Tính theo năm",
    },
    {
      label: "Tỷ lệ Sharpe",
      value: fmtNumber(sharpeRatio),
      color: getPnlColor(sharpeRatio || 0),
      subLabel: `Đánh giá: ${sharpeStatus(sharpeRatio || 0).label}`,
    },
  ];
};

export const HOLDING_PAGE_SIZE = 5;
export const HOLDING_NO_PAGINATION_MAX = 10;

export const resolveHoldingPageSize = (total = 0) =>
  total > 0 && total <= HOLDING_NO_PAGINATION_MAX
    ? HOLDING_NO_PAGINATION_MAX
    : HOLDING_PAGE_SIZE;

export const holdingTotalPages = (total = 0, pageSize = HOLDING_PAGE_SIZE) =>
  Math.ceil((total || 0) / pageSize) || 1;

export const isHoldingPaginationHidden = (total = 0) =>
  (total || 0) <= HOLDING_NO_PAGINATION_MAX;
